import type { Widget, WidgetDefinitionNew } from '@neptune/shared/widgets-domain';

import { AttributeType } from 'domain/experiment/attribute';
import { WidgetDefinition } from 'domain/widget/core/domain/widget-definition';

import {
  DEFAULT_TABLE_CONFIG,
  isTableWidgetDisplayMode,
  type TableWidgetDisplayMode,
  type TableWidgetOptions,
} from './table-configuration';

export type TableWidget = Widget & {
  type: 'table';
  options: TableWidgetOptions;
};

export const COMPATIBLE_ATTRIBUTE_TYPES: AttributeType[] = ['floatSeries'];

export const TABLE_WIDGET_DEFINITION_NEW: WidgetDefinitionNew = {
  widgetType: 'table',
  label: 'Table',
  description: 'Compare numeric series across a step range',
  icon: 'table',
  dataItem: 'table',
  hasSettings: true,
};

export function isTableWidget(widget: Widget): widget is TableWidget {
  return widget.type === 'table';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getNumberOrDefault(value: unknown, defaultValue: number | undefined) {
  return typeof value === 'number' ? value : defaultValue;
}

function getDisplayModeOrDefault(value: unknown): TableWidgetDisplayMode {
  return isTableWidgetDisplayMode(value) ? value : DEFAULT_TABLE_CONFIG.displayMode;
}

export function normalizeTableWidgetOptions(options?: Record<string, unknown>): TableWidgetOptions {
  const stepRange = isRecord(options?.stepRange) ? options.stepRange : undefined;

  return {
    displayMode: getDisplayModeOrDefault(options?.displayMode),
    stepRange: {
      start: getNumberOrDefault(stepRange?.start, DEFAULT_TABLE_CONFIG.stepRange.start),
      end: getNumberOrDefault(stepRange?.end, DEFAULT_TABLE_CONFIG.stepRange.end),
    },
  };
}

export function tableWidgetOptionsFromApiToDomain(
  options?: Record<string, unknown>,
): TableWidgetOptions {
  return normalizeTableWidgetOptions(options);
}

export function tableWidgetOptionsFromDomainToApi(
  options?: TableWidgetOptions,
): TableWidgetOptions | undefined {
  if (!options) {
    return undefined;
  }

  return {
    ...options,
    displayMode: getDisplayModeOrDefault(options.displayMode),
  };
}

export const TABLE_WIDGET_DEFINITION: WidgetDefinition = {
  type: 'table',
  fromApiToDomain: tableWidgetOptionsFromApiToDomain,
  fromDomainToApi: tableWidgetOptionsFromDomainToApi,
};
